import { ErrorMessage } from "@/components/ErrorMessage";
import { Banner } from "@/components/flexkit/Banner";
import { DivContainer, TimelineContentContainer } from "@/components/onboarding/TimelineContentContainer";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { SourceControlProvider } from "@/components/runners/details/SourceControlProvider";
import { TrackLocations } from "@/hooks/use-segment";
import { useListRunnerSCMIntegrations, useRunnerConfigurationSchema } from "@/queries/runner-configuration-queries";
import { useRunner } from "@/queries/runner-queries";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { type FC, type PropsWithChildren } from "react";
import { useNavigate, useParams } from "react-router-dom";

export const ConfigureSCMPage: FC = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { data: runner, isPending: isPendingRunner, error: runnerError } = useRunner(params.runnerId);
    const {
        data: schema,
        isPending: schemaIsPending,
        error: schemaError,
    } = useRunnerConfigurationSchema(runner?.runnerId);
    const {
        data: integrations,
        isPending: integrationIsPending,
        error: integrationError,
    } = useListRunnerSCMIntegrations(runner?.runnerId);

    const isPending = isPendingRunner || schemaIsPending || integrationIsPending;
    const hasStack = runner?.status?.phase !== RunnerPhase.CREATED;
    const canContinue = !integrationIsPending && !!integrations?.length;

    return (
        <TimelineContentContainer
            title="Configure Git provider"
            description="In order for your runner to access your repositories, you’ll need to configure your Git provider (GitHub, GitLab, Bitbucket or Azure DevOps). Select one of the providers and continue to set up."
            onBack={() => navigate(`/onboarding/set-up-runner/remote/configure/${params.runnerId}/install`)}
            onNext={() => navigate(`/onboarding/set-up-runner/remote/configure/${params.runnerId}/environment-classes`)}
            onNextDisabled={!canContinue}
            docsLink="https://www.gitpod.io/docs/flex/source-control"
            data-track-location={TrackLocations.OnboardingRunnerConfigureSCMTabStep}
        >
            <ErrorMessage error={runnerError} />
            <ErrorMessage error={schemaError} />
            <ErrorMessage error={integrationError} />

            <Skeleton ready={!!runner && !isPending}>
                <div className="flex flex-col gap-2">
                    {canContinue && <Banner variant="success" text="Git provider configured" />}
                    {runner && (
                        <SourceControlProvider
                            hasStack={hasStack}
                            runner={runner}
                            schemas={schema?.scm}
                            Container={DivContainer}
                            withDescription={false}
                            data-track-location={(modalState) =>
                                modalState.type == "add-provider"
                                    ? TrackLocations.OnboardingRunnerAddSCMProviderModal
                                    : modalState.type == "remove-provider"
                                      ? TrackLocations.OnboardingRunnerDeleteSCMProviderModal
                                      : TrackLocations.OnboardingRunnerEditSCMProviderModal
                            }
                        />
                    )}
                </div>
            </Skeleton>
        </TimelineContentContainer>
    );
};

const Skeleton: FC<PropsWithChildren<{ ready: boolean }>> = ({ ready, children }) => {
    if (ready) {
        return children;
    }
    return (
        <div className="flex w-full flex-col gap-2">
            <SkeletonBlock className="h-[49px] w-full" ready={false} />
            <SkeletonBlock className="h-[49px] w-full" ready={false} />
            <SkeletonBlock className="h-[49px] w-full" ready={false} />
        </div>
    );
};
